import { getAgentPackages } from "../services/fhpService";

/**
 * Single place: numeric package id for POST /api/register-agent/draft comes from
 * getAgentPackages() when the API exposes a numeric id; otherwise numeric package_id string.
 * Last resort only if catalog missing (offline / error).
 */
// AGENCY:3 מקביל ל-fallback ב-getAgentPackages כשאין DB; בפרוד ה-id מגיע מהקטלוג.
const FALLBACK_CODE_TO_ID = {
  BASIC: 1,
  PRO: 2,
  AGENCY: 3,
  TRIAL: 4,
};

let catalogPromise = null;

export function buildCatalogFromPackagesResponse(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((pkg) => ({
    code: pkg.package_id != null ? String(pkg.package_id).trim().toUpperCase() : "",
    numericId: pickNumericFromPackageRow(pkg),
  }));
}

function pickNumericFromPackageRow(pkg) {
  if (!pkg || typeof pkg !== "object") return null;
  const candidateKeys = [
    "billing_package_id",
    "billingPackageId",
    "numeric_id",
    "numericId",
    "db_id",
    "dbId",
    "id",
  ];
  for (const key of candidateKeys) {
    const v = pkg[key];
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const pid = pkg.package_id;
  if (pid != null && pid !== "") {
    const n = Number(pid);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export async function loadAgentPackagesCatalog() {
  if (!catalogPromise) {
    catalogPromise = (async () => {
      try {
        const response = await getAgentPackages();
        const raw = response?.data?.packages;
        return buildCatalogFromPackagesResponse(raw);
      } catch {
        return [];
      }
    })();
  }
  return catalogPromise;
}

/**
 * @param {string} packageCode e.g. PRO, BASIC
 * @param {Array<{ code: string, numericId: number | null }>} [catalog] optional preloaded
 */
export async function resolveBillingPackageIdAsync(packageCode) {
  const catalog = await loadAgentPackagesCatalog();
  return resolveBillingPackageId(packageCode, catalog);
}

export function resolveBillingPackageId(packageCode, catalog = null) {
  const rawCode = packageCode == null ? "" : String(packageCode);
  const normalizedCode = rawCode.trim().toUpperCase();
  const code = normalizedCode === "STARTER" ? "BASIC" : normalizedCode;
  if (code === "") {
    const fb = FALLBACK_CODE_TO_ID.PRO;
    return { packageId: fb, source: "fallback_empty" };
  }
  const nDirect = Number(code);
  if (Number.isFinite(nDirect) && nDirect > 0) {
    return { packageId: nDirect, source: "numeric_code" };
  }
  const list = catalog || [];
  const row = list.find((p) => p.code === code);
  if (row && row.numericId != null) {
    return { packageId: row.numericId, source: "api_catalog" };
  }
  if (code === "TRIAL") {
    return { packageId: FALLBACK_CODE_TO_ID.TRIAL, source: "fallback_trial" };
  }
  if (code === "AGENCY") {
    const agencyRow = list.find((p) => p.code === "AGENCY");
    if (agencyRow && agencyRow.numericId != null) {
      return { packageId: agencyRow.numericId, source: "api_catalog" };
    }
    const fb = FALLBACK_CODE_TO_ID.AGENCY;
    return { packageId: fb, source: "fallback_agency_numeric" };
  }
  const upper = code.toUpperCase();
  const fb = FALLBACK_CODE_TO_ID[upper];
  if (fb != null) {
    return { packageId: fb, source: "fallback_map" };
  }
  return { packageId: FALLBACK_CODE_TO_ID.PRO, source: "fallback_default" };
}
